import { useState } from "react";
import "../../../styles/studio/folder/studioFolderMeta.css";
import { deleteFolder, updateFolder } from "../../../services/folder.service";
import ErrorToast from "../../common/ErrorToast";
import PricingSection from "../PricingSection";

const StudioFolderMeta = ({
  folderMetaData,
  folderId,
  parentPath = [],
  navigate,
  globalLoading,
  setGlobalLoading,
  error,
  setError,
}) => {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(folderMetaData.title);
  const [description, setDescription] = useState(folderMetaData.description || "");
  const [saving, setSaving] = useState(false);
  const [pricing, setPricing] = useState({
    accessType: folderMetaData.accessType || "free",
    price: folderMetaData.price || 0,
    currency: folderMetaData.currency || "USD",
    pricingModel: folderMetaData.pricingModel || "lifetime",
  });

  const isRootFolder = parentPath.length === 0;

  /* -------- SAVE META -------- */
  const handleSave = async () => {
    if (!title.trim() || globalLoading) return;

    setSaving(true);
    setGlobalLoading(true); // 🔒 block entire app

    try {
      await updateFolder(folderId, { title, description });
      setEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
      setGlobalLoading(false); // 🔓 unlock
    }
  };

  /* -------- DELETE FOLDER -------- */
  const handleDelete = async () => {
    if (isRootFolder || globalLoading) return;

    if (
      !confirm(
        "Are you sure you want to delete this folder? This will not delete its items.",
      )
    )
      return;

    setGlobalLoading(true); // 🔒 lock UI

    try {
      await deleteFolder(folderId);

      let parentIdPath = "";
      for (let i = 1; i < parentPath.length; i++) {
        parentIdPath += parentPath[i].folderId;
        if (i < parentPath.length - 1) parentIdPath += "/";
      }

      navigate(
        parentIdPath ? `/studio/folders/${parentIdPath}` : `/studio/folders/`,
      );
    } catch (err) {
      setError(err.message);
      setGlobalLoading(false); // only unlock on error (navigation handles success case)
    }
  };

  return (
    <section className="studio-folder-meta">
      {editing ? (
        <div className="studio-folder-meta-edit">
          <input
            type="text"
            className="studio-folder-meta-input-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={saving || globalLoading}
          />
          <textarea
            className="studio-folder-meta-input-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={saving || globalLoading}
          />
          <div className="studio-folder-meta-actions">
            <button
              className="studio-folder-meta-btn-save"
              onClick={handleSave}
              disabled={saving || globalLoading}
            >
              Save
            </button>
            <button
              className="studio-folder-meta-btn-cancel"
              onClick={() => setEditing(false)}
              disabled={saving || globalLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="studio-folder-meta-display">
          <h1 className="studio-folder-meta-title" title={title}>
            {title}
          </h1>
          {description && (
            <p className="studio-folder-meta-description" title={description}>
              {description}
            </p>
          )}
          <div className="studio-folder-meta-actions">
            <button
              className="studio-folder-meta-btn-edit"
              onClick={() => !globalLoading && setEditing(true)}
              disabled={globalLoading}
            >
              Edit
            </button>
            <button
              className="studio-folder-meta-btn-delete"
              onClick={handleDelete}
              disabled={globalLoading || isRootFolder}
              title={
                isRootFolder ? "Cannot delete root folder" : "Delete Folder"
              }
            >
              Delete Folder
            </button>
          </div>
        </div>
      )}
      {!isRootFolder && (
        <PricingSection
          contentType="folder"
          contentId={folderId}
          pricing={pricing}
          setPricing={setPricing}
        />
      )}
      {error && <ErrorToast error={error} background={false} />}
    </section>
  );
};

export default StudioFolderMeta;
